import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  TrendingUp,
  AlertCircle,
  FileCheck,
  UserX,
  UserCheck,
  CalendarClock,
  Hourglass,
  Download,
  Loader2,
  CalendarDays,
  CalendarRange,
  CalendarSearch,
  ArrowUp,
  ArrowDown,
  Flame,
  ChevronDown,
  ChevronUp,
  Check,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast } from '../../Components/Feedback';
import MonthlyTrendChart from '../../Components/MonthlyTrendChart';
import AreaFrequencyChart from '../../Components/AreaFrequencyChart';
import BusCapacityIcon from '../../Components/BusCapacityIcon';
import { AREAS, AREA_LABEL } from '../../utils/areas';
import { API_URL } from '../../api/config';

// Dashboard de frequência (rota /relatorio-diario): estatísticas do dia,
// comparativo por turno/transporte, estatísticas de período e exportação em Excel
// (lista de ausentes do dia e planilha de frequência por período).
const getWeekdayFromDate = (dateStr) => {
  const dias = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
  const date = new Date(dateStr + 'T12:00:00');
  return dias[date.getDay()];
};

// Cache em memória para evitar re-fetch de dados já carregados na sessão
const memoryCache = {};
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

const getCached = (key) => {
  const entry = memoryCache[key];
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    delete memoryCache[key];
    return null;
  }
  return entry.data;
};
const setCache = (key, data) => {
  memoryCache[key] = { data, ts: Date.now() };
};

// `comparativo` (opcional): { delta, sufixo, invertido } pra mostrar ▲/▼ vs.
// mês anterior — verde quando sobe / vermelho quando cai por padrão; com
// `invertido: true` (ex.: card de Faltas, onde subir é ruim) essa cor
// inverte. `onClick` (opcional) vira o card num botão (ex.: "Ativos sem
// Matrícula", que abre a lista de quem são) — mesmo visual, só com
// cursor/hover indicando que é clicável.
const KpiCard = ({ icon: Icon, label, value, color, sublabel, comparativo, onClick }) => {
  const colorMap = {
    blue: {
      icon: 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-blue-500/30',
      glow: 'group-hover:shadow-blue-500/20',
    },
    purple: {
      icon: 'bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-purple-500/30',
      glow: 'group-hover:shadow-purple-500/20',
    },
    green: {
      icon: 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-emerald-500/30',
      glow: 'group-hover:shadow-emerald-500/20',
    },
    red: {
      icon: 'bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-red-500/30',
      glow: 'group-hover:shadow-red-500/20',
    },
    amber: {
      icon: 'bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-amber-500/30',
      glow: 'group-hover:shadow-amber-500/20',
    },
    cyan: {
      icon: 'bg-gradient-to-br from-cyan-500 to-cyan-600 text-white shadow-cyan-500/30',
      glow: 'group-hover:shadow-cyan-500/20',
    },
    orange: {
      icon: 'bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-orange-500/30',
      glow: 'group-hover:shadow-orange-500/20',
    },
    pink: {
      icon: 'bg-gradient-to-br from-pink-500 to-pink-600 text-white shadow-pink-500/30',
      glow: 'group-hover:shadow-pink-500/20',
    },
    gray: {
      icon: 'bg-gradient-to-br from-gray-400 to-gray-500 text-white shadow-gray-400/30',
      glow: 'group-hover:shadow-gray-400/20',
    },
  };
  const c = colorMap[color] || colorMap.blue;
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`group relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4 flex items-center justify-between gap-3 w-full h-full text-left transition-all duration-200 ${onClick ? 'hover:-translate-y-0.5 hover:shadow-lg cursor-pointer' : `hover:shadow-md ${c.glow}`}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="text-[28px] leading-none font-extrabold text-gray-900 dark:text-gray-100 tracking-tight">
            {value}
          </div>
          {comparativo &&
            comparativo.delta !== 0 &&
            (() => {
              const subiu = comparativo.delta > 0;
              const eBom = comparativo.invertido ? !subiu : subiu;
              return (
                <span
                  className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-lg text-[10px] font-semibold ${eBom ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'}`}
                  title="Comparado ao mês anterior completo"
                >
                  {subiu ? (
                    <ArrowUp className="w-2.5 h-2.5" />
                  ) : (
                    <ArrowDown className="w-2.5 h-2.5" />
                  )}
                  {Math.abs(comparativo.delta)}
                  {comparativo.sufixo || ''}
                </span>
              );
            })()}
        </div>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-1.5 truncate">
          {label}
        </div>
        {sublabel && (
          <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 leading-snug">
            {sublabel}
          </div>
        )}
      </div>
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${c.icon}`}
      >
        <Icon className="w-6 h-6" strokeWidth={2.25} />
      </div>
    </Wrapper>
  );
};

const StatBox = ({ label, value, tone }) => {
  const toneMap = {
    green: {
      bg: 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-900/40 text-green-700 dark:text-green-400',
      bar: 'bg-emerald-500',
    },
    red: {
      bg: 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-400',
      bar: 'bg-red-500',
    },
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900/40 text-blue-700 dark:text-blue-400',
      bar: 'bg-blue-500',
    },
    amber: {
      bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-900/40 text-amber-700 dark:text-amber-400',
      bar: 'bg-amber-500',
    },
    gray: {
      bg: 'bg-gray-50 dark:bg-gray-800/60 border-gray-100 dark:border-gray-800 text-gray-700 dark:text-gray-300',
      bar: 'bg-gray-400',
    },
  };
  const t = toneMap[tone] || toneMap.gray;
  return (
    <div className={`relative rounded-2xl border p-3 pl-4 overflow-hidden ${t.bg}`}>
      <span className={`absolute left-0 top-0 bottom-0 w-1 ${t.bar}`} />
      <div className="text-xs opacity-80 mb-1">{label}</div>
      <div className="text-xl font-bold">{value ?? 0}</div>
    </div>
  );
};

// Anel de presença do dia (substitui o antigo círculo em conic-gradient, que
// não dava pra suavizar as pontas nem adicionar brilho/gradiente). Duas
// fatias (presentes/ausentes) desenhadas com o truque clássico de
// strokeDasharray + strokeDashoffset em dois <circle> sobrepostos, girados
// -90° pra começar no topo — mesmos dois números de antes, só o desenho é novo.
const PresencaGauge = ({ percentualPresenca, presentes, ausentes, esperados }) => {
  const tamanho = 152;
  const raio = 58;
  const espessura = 14;
  const centro = tamanho / 2;
  const circunferencia = 2 * Math.PI * raio;
  const fracaoPresente = esperados > 0 ? Math.max(0, Math.min(1, presentes / esperados)) : 0;

  return (
    <div className="relative flex-shrink-0" style={{ width: tamanho, height: tamanho }}>
      <svg
        width={tamanho}
        height={tamanho}
        viewBox={`0 0 ${tamanho} ${tamanho}`}
        className="overflow-visible"
      >
        <defs>
          <linearGradient id="gaugePresente" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
          <linearGradient id="gaugeAusente" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f87171" />
            <stop offset="100%" stopColor="#dc2626" />
          </linearGradient>
          <filter id="gaugeGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle
          cx={centro}
          cy={centro}
          r={raio}
          fill="none"
          strokeWidth={espessura}
          className="stroke-gray-100 dark:stroke-gray-800"
        />

        {esperados > 0 && (
          <g transform={`rotate(-90 ${centro} ${centro})`} filter="url(#gaugeGlow)">
            <circle
              cx={centro}
              cy={centro}
              r={raio}
              fill="none"
              strokeWidth={espessura}
              strokeLinecap="round"
              stroke="url(#gaugePresente)"
              strokeDasharray={`${circunferencia * fracaoPresente} ${circunferencia}`}
            />
            {fracaoPresente < 1 && (
              <circle
                cx={centro}
                cy={centro}
                r={raio}
                fill="none"
                strokeWidth={espessura}
                strokeLinecap="round"
                stroke="url(#gaugeAusente)"
                strokeDasharray={`${circunferencia * (1 - fracaoPresente)} ${circunferencia}`}
                strokeDashoffset={-circunferencia * fracaoPresente + 2}
              />
            )}
          </g>
        )}

        <circle
          cx={centro}
          cy={centro}
          r={raio - espessura / 2 - 6}
          className="fill-white dark:fill-gray-900"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
          {percentualPresenca}%
        </span>
        <span className="text-[11px] text-gray-400 dark:text-gray-500 uppercase tracking-wide">
          Presença
        </span>
      </div>
    </div>
  );
};

const LegendItem = ({ color, label }) => (
  <div className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
    <span className={`w-2.5 h-2.5 rounded-full shadow-sm ${color}`} />
    {label}
  </div>
);

// Visão mensal (alternador Diário/Mensal) — mesmos cards de KPI do snapshot
// diário, calculados pro mês inteiro, mais o gráfico de tendência dia a dia.
const COLUNAS_FREQ_ALUNO = [
  { chave: 'nome', label: 'Inscrito', align: 'left' },
  { chave: 'dias_esperados', label: 'Dias Esperados', align: 'center' },
  { chave: 'dias_presentes', label: 'Dias Presentes', align: 'center' },
  { chave: 'dias_falta', label: 'Dias de Falta', align: 'center' },
  { chave: 'frequencia_pct', label: '% Frequência', align: 'center' },
  { chave: 'faltas_consecutivas', label: 'Faltas Seguidas', align: 'center' },
];

const NOMES_MESES_ABREV = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

// Mesma cor de cada área nos botões de filtro (Turmas.js, AjusteGrade.js,
// GradeTurmas.js — ver utils/areas.js), só remapeada pro conjunto de cores
// que o KpiCard aceita.
const COR_KPI_POR_AREA = {
  educacional: 'blue',
  esportivo: 'green',
  cultural: 'purple',
  tecnologico: 'orange',
  capelania: 'pink',
};

// Rótulo amigável por status de aluno (ver Select em GerenciarMatriculas.js —
// mesmos valores). `status` é livre no banco (varchar), por isso o fallback
// capitaliza o que vier em vez de sumir com o valor.
const STATUS_LABEL = {
  inativo: 'Inativo',
  espera: 'Em espera',
  sem_status: 'Sem status',
};
const rotuloStatus = (status) =>
  STATUS_LABEL[status] || status.charAt(0).toUpperCase() + status.slice(1);

// Cor do número de cada status — inativo em tom neutro/de alerta leve,
// espera em âmbar (é uma situação transitória, não um problema), qualquer
// outro valor cai no neutro.
const TOM_STATUS = {
  inativo: 'text-gray-500 dark:text-gray-400',
  espera: 'text-amber-600 dark:text-amber-400',
};
const tomStatus = (status) => TOM_STATUS[status] || 'text-gray-700 dark:text-gray-300';

// A partir de quantas faltas seguidas (contando pra trás a partir do dia
// letivo mais recente, ver backend) o indicador vira alerta visual — sinal
// de risco de evasão, mais acionável que a % média do mês (um aluno pode ter
// 80% de frequência e ainda estar faltando há 2 semanas seguidas agora).
const LIMITE_FALTAS_CONSECUTIVAS_ALERTA = 3;

const MonthlyView = ({
  statsMensais,
  loadingMensal,
  rotuloMes,
  mensalEsperados,
  mensalPresentes,
  mensalAusentes,
  mensalJustificados,
  mensalFrequenciaPct,
  mensalJustificadosPctDasFaltas,
  pendenciasMes,
  finalizandoDia,
  onFinalizarDia,
  podeEditar,
}) => {
  const [buscaAlunoFreq, setBuscaAlunoFreq] = useState('');
  const [ordenacaoFreq, setOrdenacaoFreq] = useState({ coluna: 'nome', direcao: 'asc' });
  // Card "Frequência Individual por Inscrito" pode ficar grande (1 linha por
  // inscrito) — minimizado, mostra só o cabeçalho + a busca + 3 linhas, sem
  // perder o acesso rápido a eles; "Expandir" volta a mostrar a tabela inteira
  // com rolagem (comportamento de antes).
  const [freqMinimizado, setFreqMinimizado] = useState(true);
  const LINHAS_QUANDO_MINIMIZADO = 3;

  const alternarOrdenacaoFreq = (coluna) => {
    setOrdenacaoFreq((prev) =>
      prev.coluna === coluna
        ? { coluna, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' }
        : { coluna, direcao: coluna === 'nome' ? 'asc' : 'desc' },
    );
  };

  // Comparação com o mês anterior completo (ver GET /estatisticas-mensais,
  // campo mes_anterior) — "Frequência" compara na mesma base agregada dos
  // dois lados (presentes/esperados em registros), não a média das %
  // individuais que o card mostra, porque essa média só existe pro mês atual;
  // a seta ainda indica a tendência certa, só a precisão decimal que difere.
  const mesAnterior = statsMensais?.mes_anterior;

  // Alunos Únicos (Pessoas Físicas)
  const esperadosAlunos = statsMensais?.total_esperados_alunos ?? 0;
  const presentesAlunos = statsMensais?.total_presentes_alunos ?? 0;
  const ausentesAlunos = statsMensais?.total_ausentes_alunos ?? 0;
  const justificadosAlunos = statsMensais?.total_justificados ?? 0;

  // Registros Acumulados (Aluno-Dia)
  const esperadosRegistros = statsMensais?.total_esperados_registros ?? mensalEsperados;
  const presentesRegistros = statsMensais?.total_presentes_registros ?? mensalPresentes;
  const faltasRegistros = statsMensais?.total_faltas_registros ?? mensalAusentes;
  const justificativasRegistros = statsMensais?.total_justificativas_registros ?? mensalJustificados;

  // Comparativos com o mês anterior (apenas quando ambos forem alunos únicos)
  const comparativoEsperados =
    mesAnterior && typeof mesAnterior.total_esperados_alunos === 'number'
      ? { delta: esperadosAlunos - mesAnterior.total_esperados_alunos }
      : null;

  const comparativoPresentes =
    mesAnterior && typeof mesAnterior.total_presentes_alunos === 'number'
      ? { delta: presentesAlunos - mesAnterior.total_presentes_alunos }
      : null;

  const frequenciaAnterior =
    typeof mesAnterior?.media_frequencia_individual === 'number'
      ? mesAnterior.media_frequencia_individual
      : typeof mesAnterior?.frequencia_alunos_pct === 'number'
        ? mesAnterior.frequencia_alunos_pct
        : typeof mesAnterior?.frequencia_pct === 'number'
          ? Math.min(100, Math.max(0, mesAnterior.frequencia_pct))
          : null;

  const comparativoFrequencia =
    frequenciaAnterior !== null
      ? { delta: mensalFrequenciaPct - frequenciaAnterior, sufixo: 'pp' }
      : null;

  const comparativoAusentes =
    mesAnterior && typeof mesAnterior.total_ausentes_alunos === 'number'
      ? { delta: ausentesAlunos - mesAnterior.total_ausentes_alunos, invertido: true }
      : null;

  const comparativoJustificados =
    mesAnterior && typeof mesAnterior.total_justificados_alunos === 'number'
      ? { delta: justificadosAlunos - mesAnterior.total_justificados_alunos }
      : null;

  const frequenciaPorAlunoFiltrada = (statsMensais?.frequencia_por_aluno ?? [])
    .filter((a) => !buscaAlunoFreq || a.nome.toLowerCase().includes(buscaAlunoFreq.toLowerCase()))
    .slice()
    .sort((a, b) => {
      const { coluna, direcao } = ordenacaoFreq;
      const mult = direcao === 'asc' ? 1 : -1;
      if (coluna === 'nome') return a.nome.localeCompare(b.nome, 'pt-BR') * mult;
      return (a[coluna] - b[coluna]) * mult;
    });

  if (loadingMensal && !statsMensais) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-3" /> Carregando {rotuloMes}...
      </div>
    );
  }

  return (
    <>
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Performance do Mês
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {rotuloMes} · {statsMensais?.total_dias_letivos ?? 0} dias letivos
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard
          icon={CalendarClock}
          label="Esperados no Mês"
          value={esperadosAlunos}
          color="blue"
          sublabel={`${esperadosRegistros} presenças previstas (${statsMensais?.total_dias_letivos ?? 0} dias letivos)`}
          comparativo={comparativoEsperados}
        />
        <KpiCard
          icon={UserCheck}
          label="Presentes no Mês"
          value={presentesAlunos}
          color="purple"
          sublabel={`${presentesRegistros} presenças realizadas no total`}
          comparativo={comparativoPresentes}
        />
        <KpiCard
          icon={TrendingUp}
          label="Frequência do Mês"
          value={`${mensalFrequenciaPct}%`}
          color="green"
          sublabel="Média de assiduidade dos alunos"
          comparativo={comparativoFrequencia}
        />
        <KpiCard
          icon={AlertCircle}
          label="Sem Frequência no Mês"
          value={ausentesAlunos}
          color="red"
          sublabel={`${faltasRegistros} faltas acumuladas em chamadas`}
          comparativo={comparativoAusentes}
        />
        <KpiCard
          icon={FileCheck}
          label="Justificados no Mês"
          value={justificadosAlunos}
          color="amber"
          sublabel={`${justificativasRegistros} faltas abonadas (${mensalJustificadosPctDasFaltas}% das faltas)`}
          comparativo={comparativoJustificados}
        />
      </div>
      {mesAnterior && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-2">
          Comparado a {NOMES_MESES_ABREV[Number(mesAnterior.data_inicio.slice(5, 7)) - 1]}/
          {mesAnterior.data_inicio.slice(0, 4)} (mês completo)
        </p>
      )}

      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Chamadas Não Finalizadas
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Dias já passados com inscrito esperado sem nenhum registro de presença
          </p>
        </div>
        <div className="p-3">
          {pendenciasMes?.loading ? (
            <div className="flex items-center justify-center py-6 text-gray-400 dark:text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : !pendenciasMes?.lista?.length ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
              Nenhuma pendência — todas as chamadas do mês (até hoje) estão finalizadas.
            </p>
          ) : (
            <div className="space-y-1.5">
              {pendenciasMes.lista.map((dia) => {
                const processando = finalizandoDia === dia.data;
                return (
                  <div
                    key={dia.data}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/40"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {dia.data.split('-').reverse().join('/')}
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        {dia.turnos.map((t) => `${t.turno} (${t.pendentes})`).join(' · ')}
                      </p>
                    </div>
                    {podeEditar && (
                      <button
                        onClick={() => onFinalizarDia(dia.data)}
                        disabled={processando}
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-all disabled:opacity-60 flex-shrink-0"
                      >
                        {processando ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )}
                        Finalizar
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Frequência ao Longo do Mês
          </h3>
        </div>
        <div className="p-3">
          <MonthlyTrendChart dados={statsMensais?.tendencia_diaria ?? []} />
        </div>
      </section>

      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Frequência por Área
          </h3>
        </div>
        <div className="p-3">
          <AreaFrequencyChart dados={statsMensais?.tendencia_por_area ?? []} />
        </div>
      </section>

      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Frequência Individual por Inscrito
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Dias presentes ÷ dias esperados de cada inscrito no mês · média entre os inscritos:{' '}
              <strong className="text-gray-700 dark:text-gray-300">
                {statsMensais?.media_frequencia_individual ?? 0}%
              </strong>
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar inscrito..."
              value={buscaAlunoFreq}
              onChange={(e) => setBuscaAlunoFreq(e.target.value)}
              className="h-9 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all flex-1 sm:w-56"
            />
            <button
              type="button"
              onClick={() => setFreqMinimizado((m) => !m)}
              title={freqMinimizado ? 'Expandir' : 'Minimizar'}
              className="h-9 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5 text-sm flex-shrink-0"
            >
              {freqMinimizado ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronUp className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">{freqMinimizado ? 'Expandir' : 'Minimizar'}</span>
            </button>
          </div>
        </div>
        <div className={freqMinimizado ? '' : 'max-h-[32rem] overflow-auto'}>
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900">
              <tr>
                {COLUNAS_FREQ_ALUNO.map((col) => {
                  const ativa = ordenacaoFreq.coluna === col.chave;
                  return (
                    <th
                      key={col.chave}
                      onClick={() => alternarOrdenacaoFreq(col.chave)}
                      className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100 transition-colors ${col.align === 'center' ? 'text-center' : 'text-left'}`}
                    >
                      {col.label}
                      {ativa && (ordenacaoFreq.direcao === 'asc' ? ' ▲' : ' ▼')}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {frequenciaPorAlunoFiltrada.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center text-gray-400 dark:text-gray-500"
                  >
                    Nenhum inscrito encontrado.
                  </td>
                </tr>
              ) : (
                (freqMinimizado
                  ? frequenciaPorAlunoFiltrada.slice(0, LINHAS_QUANDO_MINIMIZADO)
                  : frequenciaPorAlunoFiltrada
                ).map((a) => {
                  const emAlerta = a.faltas_consecutivas >= LIMITE_FALTAS_CONSECUTIVAS_ALERTA;
                  return (
                    <tr
                      key={a.aluno_id}
                      className="border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50/60 dark:hover:bg-gray-800/40"
                    >
                      <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100" translate="no">
                        {a.nome}
                      </td>
                      <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-400">
                        {a.dias_esperados}
                      </td>
                      <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-400">
                        {a.dias_presentes}
                      </td>
                      <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-400">
                        {a.dias_falta}
                      </td>
                      <td className="px-4 py-2.5 text-center font-semibold text-gray-900 dark:text-gray-100">
                        {a.frequencia_pct}%
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {emAlerta ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-semibold"
                            title="3 ou mais dias letivos seguidos sem presença — risco de evasão"
                          >
                            <Flame className="w-3 h-3" /> {a.faltas_consecutivas}
                          </span>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">
                            {a.faltas_consecutivas || 0}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {freqMinimizado && frequenciaPorAlunoFiltrada.length > LINHAS_QUANDO_MINIMIZADO && (
          <button
            type="button"
            onClick={() => setFreqMinimizado(false)}
            className="w-full px-3 py-2 text-xs text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 border-t border-gray-100 dark:border-gray-800 transition-colors"
          >
            + {frequenciaPorAlunoFiltrada.length - LINHAS_QUANDO_MINIMIZADO} inscrito
            {frequenciaPorAlunoFiltrada.length - LINHAS_QUANDO_MINIMIZADO === 1 ? '' : 's'} oculto
            {frequenciaPorAlunoFiltrada.length - LINHAS_QUANDO_MINIMIZADO === 1 ? '' : 's'} · clique
            pra expandir
          </button>
        )}
      </section>

      {statsMensais?.justificativas?.length > 0 && (
        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Justificativas do Mês por Tipo
            </h3>
          </div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {statsMensais.justificativas.map((j, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between px-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800 text-sm"
              >
                <span className="text-gray-700 dark:text-gray-300">{j.tipo}</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {j.quantidade}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

const DailyReport = () => {
  const { institutionId } = useInstitution();
  const { podeEditar, podeExportar } = usePermissao('/relatorio-diario');
  const toast = useToast();
  const location = useLocation();

  const getTodayLocal = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now - offset).toISOString().split('T')[0];
  };

  const [dataSelecionada, setDataSelecionada] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    const dateFromQuery = searchParams.get('date');
    const dateFromState = location.state?.dataAtual;
    return dateFromState || dateFromQuery || getTodayLocal();
  });

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null); // agregados vindos do backend

  // Alternador Diário/Mensal (ver GET /api/relatorios/estatisticas-mensais) —
  // "Consolidado do Período" continua visível nos dois modos porque já é
  // independente de dia/mês selecionado.
  const [modo, setModo] = useState('diario'); // 'diario' | 'mensal'
  const [mesSelecionado, setMesSelecionado] = useState(() => getTodayLocal().slice(0, 7));
  const [statsMensais, setStatsMensais] = useState(null);
  const [loadingMensal, setLoadingMensal] = useState(false);

  const [periodoStats, setPeriodoStats] = useState(null);
  const [periodoDataInicio, setPeriodoDataInicio] = useState('');
  const [periodoDataFim, setPeriodoDataFim] = useState('');
  const [loadingPeriodo, setLoadingPeriodo] = useState(false);

  const [historicoGeral, setHistoricoGeral] = useState({
    mediaPeriodo: 0,
    loading: true,
  });

  // Histórico: uma única requisição leve por instituição, cacheada (ver
  // GET /api/relatorios/historico-geral em backend/relatorios.js).
  useEffect(() => {
    if (!institutionId || institutionId === 'undefined') {
      setHistoricoGeral((prev) => ({ ...prev, loading: false }));
      return;
    }
    const cacheKey = `historico_${institutionId}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setHistoricoGeral({ ...cached, loading: false });
      return;
    }
    axios
      .get(`${API_URL}/api/relatorios/historico-geral`, {
        headers: { 'x-institution-id': String(institutionId) },
      })
      .then((res) => {
        const d = res.data?.data ?? res.data ?? {};
        const result = { mediaPeriodo: d.media_frequencia || 0 };
        setCache(cacheKey, result);
        setHistoricoGeral({ ...result, loading: false });
      })
      .catch(() => setHistoricoGeral((prev) => ({ ...prev, loading: false })));
  }, [institutionId]);

  // Data de referência do card "Alunos por Área": o dia exato no modo Diário,
  // o último dia do mês selecionado no Mensal (hoje, se o mês ainda não
  // acabou), e o fim do intervalo no Período — sempre "a situação como
  // estava naquele dia", nunca depois dele. Sem período/mês escolhido ainda
  // (período antes da primeira busca), cai em `null` e o backend responde
  // com o total ATUAL (mesmo comportamento de antes desta mudança).
  const dataReferenciaArea = useMemo(() => {
    if (modo === 'diario') return dataSelecionada;
    if (modo === 'mensal') {
      const [ano, mes] = mesSelecionado.split('-').map(Number);
      const ultimoDiaMes = new Date(Date.UTC(ano, mes, 0, 12)).toISOString().split('T')[0];
      const hoje = getTodayLocal();
      return ultimoDiaMes < hoje ? ultimoDiaMes : hoje;
    }
    if (modo === 'periodo') return periodoDataFim || null;
    return null;
  }, [modo, dataSelecionada, mesSelecionado, periodoDataFim]);

  // Legenda dessa mesma data, pro texto abaixo do título dos dois cards
  // "Alunos por Área" (Diário e Mensal/Período) — mesmo formato dd/mm/aaaa
  // já usado em "Detalhes por Turno" nesta tela.
  const legendaDataReferenciaArea = dataReferenciaArea
    ? `em ${dataReferenciaArea.split('-').reverse().join('/')}`
    : 'agora';

  // Matrículas ativas por área NA DATA DE REFERÊNCIA acima — dado estrutural
  // (não depende de presença), mas agora histórico: reaproveita a mesma
  // lógica de intervalo (`data_inicio`/`data_fim`) já usada em "Esperados"
  // pra reconstruir quem tinha matrícula aberta naquele dia específico, não
  // só "agora" (ver GET /api/relatorios/matriculas-por-area).
  const [matriculasPorArea, setMatriculasPorArea] = useState({ porArea: {}, loading: true });

  useEffect(() => {
    if (!institutionId || institutionId === 'undefined') {
      setMatriculasPorArea((prev) => ({ ...prev, loading: false }));
      return;
    }
    const cacheKey = `matriculas_area_${institutionId}_${dataReferenciaArea || 'atual'}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setMatriculasPorArea({ porArea: cached, loading: false });
      return;
    }
    axios
      .get(`${API_URL}/api/relatorios/matriculas-por-area`, {
        headers: { 'x-institution-id': String(institutionId) },
        params: dataReferenciaArea ? { data: dataReferenciaArea } : {},
      })
      .then((res) => {
        const raw = res.data?.data ?? res.data ?? [];
        const porArea = Object.fromEntries((Array.isArray(raw) ? raw : []).map((r) => [r.area, r.total]));
        setCache(cacheKey, porArea);
        setMatriculasPorArea({ porArea, loading: false });
      })
      .catch(() => setMatriculasPorArea((prev) => ({ ...prev, loading: false })));
  }, [institutionId, dataReferenciaArea]);

  // Alunos por status, exceto "ativo" (esse já tem o card "Inscritos Ativos")
  // — mesmo padrão estrutural do bloco acima (ver GET /api/relatorios/alunos-por-status).
  const [alunosPorStatus, setAlunosPorStatus] = useState({ porStatus: {}, loading: true });

  useEffect(() => {
    if (!institutionId || institutionId === 'undefined') {
      setAlunosPorStatus((prev) => ({ ...prev, loading: false }));
      return;
    }
    const cacheKey = `alunos_status_${institutionId}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setAlunosPorStatus({ porStatus: cached, loading: false });
      return;
    }
    axios
      .get(`${API_URL}/api/relatorios/alunos-por-status`, {
        headers: { 'x-institution-id': String(institutionId) },
      })
      .then((res) => {
        const raw = res.data?.data ?? res.data ?? [];
        const porStatus = Object.fromEntries((Array.isArray(raw) ? raw : []).map((r) => [r.status, r.total]));
        setCache(cacheKey, porStatus);
        setAlunosPorStatus({ porStatus, loading: false });
      })
      .catch(() => setAlunosPorStatus((prev) => ({ ...prev, loading: false })));
  }, [institutionId]);

  // Carrega APENAS os agregados do dia — sem registros individuais de alunos
  const carregarDados = useCallback(async () => {
    if (!institutionId || institutionId === 'undefined') return;

    const cacheKey = `daily_${institutionId}_${dataSelecionada}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setStats(cached);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/relatorios/estatisticas-diarias`, {
        params: { data: dataSelecionada },
        headers: { 'x-institution-id': String(institutionId) },
      });
      const payload = res.data?.data ?? res.data;
      setCache(cacheKey, payload);
      setStats(payload);
    } catch (error) {
      console.error('Erro ao carregar dados do relatório:', error);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [institutionId, dataSelecionada]);

  // Carrega os agregados do mês (KPIs + série diária pro gráfico de tendência)
  const carregarDadosMensais = useCallback(async () => {
    if (!institutionId || institutionId === 'undefined') return;

    const cacheKey = `mensal_${institutionId}_${mesSelecionado}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setStatsMensais(cached);
      return;
    }

    setLoadingMensal(true);
    try {
      const res = await axios.get(`${API_URL}/api/relatorios/estatisticas-mensais`, {
        params: { mes: mesSelecionado },
        headers: { 'x-institution-id': String(institutionId) },
      });
      const payload = res.data?.data ?? res.data;
      setCache(cacheKey, payload);
      setStatsMensais(payload);
    } catch (error) {
      console.error('Erro ao carregar dados mensais do relatório:', error);
      setStatsMensais(null);
    } finally {
      setLoadingMensal(false);
    }
  }, [institutionId, mesSelecionado]);

  // Dias do mês com chamada não finalizada (ver GET /api/presenca/pendencias-mes)
  // — evita ter que navegar dia a dia na Chamada pra descobrir quais estão
  // pendentes. `finalizandoDia` guarda a data em processamento (desabilita só
  // o botão daquele dia, não a lista inteira).
  const [pendenciasMes, setPendenciasMes] = useState({ lista: [], loading: true });
  const [finalizandoDia, setFinalizandoDia] = useState(null);

  const carregarPendenciasMes = useCallback(async () => {
    if (!institutionId || institutionId === 'undefined') return;
    setPendenciasMes((prev) => ({ ...prev, loading: true }));
    try {
      const res = await axios.get(`${API_URL}/api/presenca/pendencias-mes`, {
        params: { mes: mesSelecionado },
        headers: { 'x-institution-id': String(institutionId) },
      });
      setPendenciasMes({ lista: res.data?.data?.dias ?? res.data?.dias ?? [], loading: false });
    } catch (error) {
      console.error('Erro ao carregar pendências do mês:', error);
      setPendenciasMes({ lista: [], loading: false });
    }
  }, [institutionId, mesSelecionado]);

  useEffect(() => {
    if (modo === 'mensal') carregarPendenciasMes();
  }, [modo, carregarPendenciasMes]);

  // Finaliza todos os turnos pendentes de um dia com um clique só (ver POST
  // /api/presenca/finalizar-dia) — recarrega a lista de pendências depois,
  // pra o dia sumir dela assim que finalizado.
  const finalizarDiaPendente = async (data) => {
    setFinalizandoDia(data);
    try {
      const res = await axios.post(
        `${API_URL}/api/presenca/finalizar-dia`,
        { data },
        {
          headers: { 'x-institution-id': String(institutionId) },
        },
      );
      const payload = res.data?.data ?? res.data;
      const dataFormatada = data.split('-').reverse().join('/');
      toast.success(
        payload?.ausentes_registrados > 0
          ? `Chamada de ${dataFormatada} finalizada — ${payload.ausentes_registrados} ausência(s) registrada(s).`
          : `Chamada de ${dataFormatada} já estava finalizada.`,
      );
      carregarPendenciasMes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao finalizar chamada do dia.');
    } finally {
      setFinalizandoDia(null);
    }
  };

  useEffect(() => {
    if (modo === 'mensal') carregarDadosMensais();
  }, [modo, carregarDadosMensais]);

  const navegarMes = (delta) => {
    const [ano, mes] = mesSelecionado.split('-').map(Number);
    const data = new Date(ano, mes - 1 + delta, 1);
    setMesSelecionado(`${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`);
  };

  const NOMES_MESES = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];
  const rotuloMes = (() => {
    const [ano, mes] = mesSelecionado.split('-').map(Number);
    return `${NOMES_MESES[mes - 1]} ${ano}`;
  })();

  // Helpers derivados de statsMensais (mesmos nomes de campo de /estatisticas-periodo)
  // Soma de aluno-dia no mês (cada dia letivo conta separado), não "quantos
  // alunos distintos apareceram pelo menos uma vez" — é o que dá pra somar
  // com a frequência individual da tabela abaixo sem virar outra pergunta.
  const mensalEsperados = statsMensais?.total_esperados_registros ?? 0;
  const mensalPresentes = statsMensais?.total_presentes_registros ?? 0;
  // "Faltas no Mês" e "Justificados no Mês" em REGISTROS (soma de aluno-dia),
  // igual Esperados/Presentes — assim Esperados - Presentes = Faltas fecha a
  // conta, e Justificados vira um subconjunto de Faltas na MESMA unidade
  // (antes eram contagem de alunos distintos: misturava unidade com os outros
  // 2 cards e não batia com a subtração). total_faltas_registros já é
  // exatamente Esperados - Presentes (ver calcularEstatisticasPeriodo).
  const mensalAusentes = statsMensais?.total_faltas_registros ?? 0;
  const mensalJustificados = statsMensais?.total_justificativas_registros ?? 0;
  const mensalJustificadosPctDasFaltas =
    mensalAusentes > 0 ? Math.round((mensalJustificados / mensalAusentes) * 100) : 0;
  // Média das % individuais de cada aluno (dias presentes ÷ dias esperados
  // DELE, não da instituição inteira) — não "quantos alunos únicos vieram
  // pelo menos uma vez" (essa segunda conta era o que "Frequência do Mês"
  // mostrava antes; ver GET /api/relatorios/estatisticas-mensais no backend).
  const mensalFrequenciaPct = statsMensais?.media_frequencia_individual ?? 0;

  // Helpers derivados de stats (sem recalcular nada em JS)
  const totalEsperado = stats?.total_esperado ?? 0;
  const totalPresentes = stats?.total_presentes ?? 0;
  const totalAusentes = stats?.total_ausentes ?? 0;
  const totalJustificados = stats?.total_justificados ?? 0;
  const totalPresencasRegistradas = stats?.total_presencas_registradas ?? 0;
  const totalPresentesReal = stats?.total_presentes_real ?? 0;
  const frequenciaPct = stats?.frequencia_pct ?? 0;
  const porTransporte = stats?.por_transporte ?? {};
  const porTurno = stats?.por_turno ?? [];
  const presentesReaisPorTurno = stats?.presentes_reais_por_turno ?? [];

  const getTurnoStats = (chave) => {
    const norm = (s) =>
      String(s || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    const rows = porTurno.filter((r) => norm(r.turno) === chave);
    const esperados = rows.reduce((s, r) => s + r.esperados, 0);
    const presentesMatriculados = rows.reduce((s, r) => s + r.presentes, 0);

    // Presentes reais incluem alunos sem matrícula para o dia
    const presentesReaisRows = presentesReaisPorTurno.filter((r) => norm(r.turno) === chave);
    const presentesReais = presentesReaisRows.reduce((s, r) => s + r.presentes_reais, 0);

    // Se houver presentes reais, usar eles pra EXIBIR "presentes" (inclui
    // quem apareceu sem matrícula prevista pro dia — número mais fiel de
    // "quantos vieram"); senão, cai pros matriculados.
    const presentes = presentesReais > 0 ? presentesReais : presentesMatriculados;

    // "Faltas" tem que ser só quem ERA esperado e não veio — por isso usa
    // sempre presentesMatriculados aqui, nunca presentesReais. Um aluno sem
    // matrícula prevista que apareceu (contado em presentesReais) não é
    // "esperado" nesse turno, então a presença dele não pode reduzir a
    // contagem de falta de quem realmente estava na lista — usar `presentes`
    // (que pode já incluir esse extra) subestimava as faltas visíveis na
    // chamada (ver relato: "5 ausências visíveis, relatório mostrando 4").
    const ausentes = Math.max(0, esperados - presentesMatriculados);
    const assiduidade = esperados > 0 ? Math.round((presentes / esperados) * 100) : 0;
    return { total: esperados, presentes, ausentes, assiduidade };
  };

  const navegarDia = (delta) => {
    const data = new Date(dataSelecionada + 'T12:00:00');
    data.setDate(data.getDate() + delta);
    setDataSelecionada(data.toISOString().split('T')[0]);
  };

  // Ver GET /api/relatorios/estatisticas-periodo em backend/relatorios.js para o
  // significado exato de cada número (mistura contagem "por aluno" e "por registro").
  const buscarEstatisticasPeriodo = async () => {
    if (!periodoDataInicio || !periodoDataFim) {
      toast.warning('Selecione as datas de início e fim do período');
      return;
    }
    if (!institutionId || institutionId === 'undefined') return;

    setLoadingPeriodo(true);
    try {
      const res = await axios.get(`${API_URL}/api/relatorios/estatisticas-periodo`, {
        params: { data_inicio: periodoDataInicio, data_fim: periodoDataFim },
        headers: { 'x-institution-id': String(institutionId) },
      });
      setPeriodoStats(res.data?.data ?? res.data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas do período:', error);
      toast.error('Erro ao buscar estatísticas do período');
    } finally {
      setLoadingPeriodo(false);
    }
  };

  // Busca nome+telefone SOMENTE quando o usuário clicar — não no carregamento da tela
  const gerarListaAusentesExcel = async () => {
    if (totalAusentes === 0) {
      toast.info('Não há inscritos ausentes neste dia.');
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/alunos/por-dia`, {
        params: { data: dataSelecionada, ignoreFilters: 'true' },
        headers: { 'x-institution-id': String(institutionId) },
      });

      const diaSemana = getWeekdayFromDate(dataSelecionada)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/-feira/g, '')
        .trim();

      const ausentes = (res.data?.data ?? res.data ?? []).filter((a) => {
        const diasMatr = String(a.dias_matriculados || '')
          .split(',')
          .map((d) =>
            d
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/-feira/g, '')
              .trim(),
          )
          .filter(Boolean);
        // Justificado fica de fora: essa lista \u00e9 usada para ligar pra casa dos
        // alunos ausentes, e quem j\u00e1 tem falta justificada n\u00e3o precisa de contato.
        return (
          diasMatr.includes(diaSemana) &&
          a.presenca_status !== 'presente' &&
          a.presenca_status !== 'justificado'
        );
      });

      if (ausentes.length === 0) {
        toast.info('Não há inscritos ausentes neste dia.');
        return;
      }

      const rows = ausentes.map((a) => ({ nome: a.nome, numero: a.telefone || '' }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 40 }, { wch: 20 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ausentes');
      XLSX.writeFile(wb, `Ausentes_${dataSelecionada}.xlsx`);
    } catch (e) {
      console.error('Erro ao gerar lista de ausentes:', e);
      toast.error('Erro ao buscar lista de ausentes: ' + (e.response?.data?.error || e.message));
    }
  };

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 p-3">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* sticky (não fixed): trava no topo da ÁREA DE SCROLL da página (o
            overflow-y-auto de Layout.jsx, que já começa abaixo do header do
            app) — não na janela inteira, então nunca sobrepõe o header.
            Fundo SÓLIDO (mesma cor da página, não opacidade fracionada) de
            propósito: com opacidade, o conteúdo que rola por baixo aparecia
            através da barra (mesmo bug já corrigido na coluna "Inscrito" do
            Ajuste de Grade — ver utils/niveis.js). */}
        <div className="sticky top-0 z-20 bg-[#F9FAFB] dark:bg-gray-950 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Título + alternador Diário/Mensal/Por Período agrupados à esquerda,
              de propósito num bloco só — assim a largura variável do seletor de
              data/mês/período (à direita, mais abaixo) nunca empurra o alternador
              de lugar quando troca de modo (era o que acontecia antes, com os
              dois dentro do mesmo grupo "justify-between": o alternador ficava
              pulando de posição toda vez que o widget ao lado mudava de tamanho). */}
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Dashboard de Frequência
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Análise Institucional</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-1 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm inline-flex items-center">
              <button
                onClick={() => setModo('diario')}
                className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold rounded-xl transition-colors ${
                  modo === 'diario'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Diário
              </button>
              <button
                onClick={() => setModo('mensal')}
                className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold rounded-xl transition-colors ${
                  modo === 'mensal'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <CalendarRange className="w-3.5 h-3.5" />
                Mensal
              </button>
              <button
                onClick={() => setModo('periodo')}
                className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold rounded-xl transition-colors ${
                  modo === 'periodo'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                <CalendarSearch className="w-3.5 h-3.5" />
                Por Período
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {modo === 'diario' ? (
              <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-1 shadow-sm">
                <button
                  onClick={() => navegarDia(-1)}
                  className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="relative flex items-center px-2">
                  <Calendar className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
                  <input
                    id="report-date"
                    type="date"
                    value={dataSelecionada}
                    onChange={(e) => setDataSelecionada(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="h-9 pl-9 pr-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => navegarDia(1)}
                  className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ) : modo === 'mensal' ? (
              <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-1 shadow-sm">
                <button
                  onClick={() => navegarMes(-1)}
                  className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="relative flex items-center px-2">
                  <Calendar className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
                  <input
                    id="report-month"
                    type="month"
                    value={mesSelecionado}
                    onChange={(e) => setMesSelecionado(e.target.value)}
                    max={getTodayLocal().slice(0, 7)}
                    className="h-9 pl-9 pr-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => navegarMes(1)}
                  disabled={mesSelecionado >= getTodayLocal().slice(0, 7)}
                  className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-800"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-1 shadow-sm">
                <div className="relative flex items-center px-2">
                  <Calendar className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
                  <input
                    type="date"
                    value={periodoDataInicio}
                    onChange={(e) => setPeriodoDataInicio(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="h-9 pl-9 pr-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                  />
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500">até</span>
                <div className="relative flex items-center px-2">
                  <Calendar className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
                  <input
                    type="date"
                    value={periodoDataFim}
                    onChange={(e) => setPeriodoDataFim(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="h-9 pl-9 pr-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                  />
                </div>
                <button
                  onClick={buscarEstatisticasPeriodo}
                  disabled={loadingPeriodo}
                  className="inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-all disabled:opacity-60 flex-shrink-0 mr-1"
                >
                  {loadingPeriodo ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <TrendingUp className="w-3.5 h-3.5" />
                  )}
                  Buscar
                </button>
              </div>
            )}
          </div>
        </div>

        {modo === 'diario' && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Performance de Hoje
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {stats?.dia_semana || 'Dia da semana'}
            </p>
          </div>
        )}

        {modo === 'diario' && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <KpiCard
                icon={Users}
                label="Inscritos Ativos"
                value={stats?.total_ativos_instituicao || 0}
                color="blue"
              />
              <KpiCard
                icon={Hourglass}
                label="Lista de Espera"
                value={alunosPorStatus.loading ? '...' : alunosPorStatus.porStatus['espera'] || 0}
                color="cyan"
              />
              <KpiCard
                icon={CalendarClock}
                label="Esperados Hoje"
                value={totalEsperado}
                color="blue"
              />
              <KpiCard
                icon={UserCheck}
                label="Presentes Registrados"
                value={totalPresencasRegistradas}
                color="purple"
                sublabel={
                  totalPresentesReal !== totalPresentes
                    ? `${totalPresentesReal} sem contar repetidos/sem matrícula p/ hoje`
                    : 'Todo registro de presença do dia'
                }
              />
              <KpiCard
                icon={TrendingUp}
                label="Frequência"
                value={`${frequenciaPct}%`}
                color="green"
              />
              <KpiCard icon={AlertCircle} label="Faltas" value={totalAusentes} color="red" />
              <KpiCard
                icon={FileCheck}
                label="Justificados"
                value={totalJustificados}
                color="amber"
              />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Alunos por Área
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Quantidade de alunos com matrícula ativa em cada área, {legendaDataReferenciaArea}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {AREAS.map((a) => (
                <KpiCard
                  key={a.value}
                  icon={Users}
                  label={AREA_LABEL[a.value] || a.value}
                  value={
                    matriculasPorArea.loading ? '...' : matriculasPorArea.porArea[a.value] || 0
                  }
                  color={COR_KPI_POR_AREA[a.value] || 'gray'}
                />
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Analisando dados em tempo real...
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Performance por Turno
                    </h3>
                  </div>
                  <div className="p-3 space-y-5">
                    {[
                      { key: 'manha', label: 'Manhã' },
                      { key: 'tarde', label: 'Tarde' },
                      { key: 'noite', label: 'Noite' },
                    ]
                      .map((t) => ({ ...t, d: getTurnoStats(t.key) }))
                      // Noite só aparece em dias que têm de fato alguma turma/ensaio
                      // esperado à noite — Manhã/Tarde continuam sempre visíveis
                      // (são os turnos "normais" do dia a dia).
                      .filter((t) => t.key !== 'noite' || t.d.total > 0)
                      .map(({ key, label, d }) => {
                        const perc = d.total > 0 ? (d.presentes / d.total) * 100 : 0;
                        return (
                          <div key={key}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {label}
                              </span>
                              <strong className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {Math.round(perc)}%
                              </strong>
                            </div>
                            <div className="h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"
                                style={{ width: `${perc}%` }}
                              />
                            </div>
                            <small className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                              {d.presentes} presentes registrados / {d.total} esperados
                            </small>
                          </div>
                        );
                      })}
                    {totalEsperado === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Nenhum inscrito esperado para este dia da semana.
                      </p>
                    )}
                  </div>
                </section>

                <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Visão Geral
                    </h3>
                  </div>
                  <div className="p-3 flex flex-col sm:flex-row items-center gap-8">
                    <PresencaGauge
                      percentualPresenca={frequenciaPct}
                      presentes={totalPresentes}
                      ausentes={totalAusentes}
                      esperados={totalEsperado}
                    />
                    <div className="space-y-2">
                      <LegendItem color="bg-green-500" label={`Presentes: ${totalPresentes}`} />
                      <LegendItem color="bg-red-500" label={`Ausentes: ${totalAusentes}`} />
                      <LegendItem
                        color="bg-gray-300 dark:bg-gray-700"
                        label={`Esperados: ${totalEsperado}`}
                      />
                    </div>
                  </div>
                  <p className="px-3 pb-3 text-[11px] text-gray-400 dark:text-gray-500">
                    Considera só quem tinha matrícula prevista pra hoje — por isso pode diferir de
                    "Presentes Registrados", que conta toda presença lançada no dia.
                  </p>
                </section>

                {/* Ocultado a pedido — mantido pronto pra reativar depois, só
                trocar `false` por `true` (fica com checagem de sintaxe/tipo
                o tempo todo, ao contrário de comentar o bloco inteiro). */}
                {false && (
                  <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm lg:col-span-2">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Presença por Transporte
                      </h3>
                    </div>
                    <div className="p-3">
                      {Object.entries(porTransporte).length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                          {Object.entries(porTransporte).map(([nome, dados]) => (
                            <div
                              key={nome}
                              className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 p-2"
                            >
                              <BusCapacityIcon
                                routeName={nome}
                                expected={dados.total}
                                real={dados.pres}
                                percentage={dados.total > 0 ? (dados.pres / dados.total) * 100 : 0}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Nenhum dado de transporte disponível.
                        </p>
                      )}
                    </div>
                  </section>
                )}
              </div>
            )}

            {totalAusentes > 0 && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-100 dark:border-red-900/40">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-700 dark:text-red-400 flex-1">
                  <strong>{totalAusentes}</strong> inscrito(s) ausente(s) neste dia
                </span>
                {podeExportar && (
                  <button
                    onClick={gerarListaAusentesExcel}
                    className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
                  >
                    <Download className="w-4 h-4" /> Baixar Lista de Ausentes
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {modo === 'mensal' && (
          <MonthlyView
            statsMensais={statsMensais}
            loadingMensal={loadingMensal}
            rotuloMes={rotuloMes}
            mensalEsperados={mensalEsperados}
            mensalPresentes={mensalPresentes}
            mensalAusentes={mensalAusentes}
            mensalJustificados={mensalJustificados}
            mensalFrequenciaPct={mensalFrequenciaPct}
            mensalJustificadosPctDasFaltas={mensalJustificadosPctDasFaltas}
            pendenciasMes={pendenciasMes}
            finalizandoDia={finalizandoDia}
            onFinalizarDia={finalizarDiaPendente}
            podeEditar={podeEditar}
          />
        )}

        {modo === 'periodo' && periodoStats && (
          <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Frequência ao Longo do Período
              </h3>
            </div>
            <div className="p-3">
              <MonthlyTrendChart dados={periodoStats.tendencia_diaria ?? []} />
            </div>
          </section>
        )}

        {modo === 'periodo' && periodoStats && (
          <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Frequência por Área
              </h3>
            </div>
            <div className="p-3">
              <AreaFrequencyChart dados={periodoStats.tendencia_por_area ?? []} />
            </div>
          </section>
        )}

        {modo === 'periodo' && (
          <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Estatísticas por Período
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Escolha as datas de início e fim ao lado e clique em Buscar
              </p>
            </div>
            <div className="p-3">
              {!periodoStats ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                  Selecione um período e clique em Buscar pra ver os números.
                </p>
              ) : (
                <div className="space-y-5">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Por Inscrito
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      <StatBox
                        label="Esperados"
                        value={periodoStats.total_esperados_alunos}
                        tone="green"
                      />
                      <StatBox
                        label="Presentes"
                        value={periodoStats.total_presentes_alunos}
                        tone="green"
                      />
                      <StatBox
                        label="Ausentes"
                        value={periodoStats.total_ausentes_alunos}
                        tone="red"
                      />
                      <StatBox
                        label="Justificados"
                        value={periodoStats.total_justificados}
                        tone="blue"
                      />
                      <StatBox
                        label="Não Justificados"
                        value={periodoStats.total_nao_justificados}
                        tone="amber"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Por Registro
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      <StatBox
                        label="Total Presenças"
                        value={periodoStats.total_presentes_registros}
                        tone="green"
                      />
                      <StatBox
                        label="Total Faltas"
                        value={periodoStats.total_faltas_registros}
                        tone="red"
                      />
                      <StatBox
                        label="Total Justificativas"
                        value={periodoStats.total_justificativas_registros}
                        tone="blue"
                      />
                      <StatBox
                        label="Dias Letivos"
                        value={periodoStats.total_dias_letivos}
                        tone="gray"
                      />
                      <StatBox
                        label="Média Inscritos/Dia"
                        value={periodoStats.media_alunos_dia}
                        tone="gray"
                      />
                    </div>
                  </div>
                  {periodoStats.justificativas && periodoStats.justificativas.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                        Justificativas por Tipo
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {periodoStats.justificativas.map((j, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between px-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800 text-sm"
                          >
                            <span className="text-gray-700 dark:text-gray-300">{j.tipo}</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {j.quantidade}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* No modo Diário essa seção já aparece logo abaixo dos cards
            principais (ver acima) — aqui só repete pra Mensal/Período, que
            não têm essa seção em nenhum outro lugar da própria tela. */}
        {modo !== 'diario' && (
          <>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Alunos por Área
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Quantidade de alunos com matrícula ativa em cada área, {legendaDataReferenciaArea}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {AREAS.map((a) => (
                <KpiCard
                  key={a.value}
                  icon={Users}
                  label={AREA_LABEL[a.value] || a.value}
                  value={
                    matriculasPorArea.loading ? '...' : matriculasPorArea.porArea[a.value] || 0
                  }
                  color={COR_KPI_POR_AREA[a.value] || 'gray'}
                />
              ))}
            </div>
          </>
        )}

        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Outros Status
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Inscritos que não estão como "Ativo", no geral (não depende do dia/mês selecionado)
            </p>
          </div>
          <div className="p-3">
            {alunosPorStatus.loading ? (
              <div className="flex items-center justify-center py-6 text-gray-400 dark:text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : Object.keys(alunosPorStatus.porStatus).length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-2">
                Nenhum inscrito em outro status.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {Object.entries(alunosPorStatus.porStatus).map(([status, total]) => (
                  <div
                    key={status}
                    className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <UserX className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                      {rotuloStatus(status)}
                    </span>
                    <span className={`text-base font-bold ${tomStatus(status)}`}>{total}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Consolidado do Período
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Histórico geral de frequência</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/30 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                Frequência Geral
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
                {historicoGeral.loading ? '...' : `${historicoGeral.mediaPeriodo}%`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyReport;
